import { useSyncExternalStore } from 'react'
import { SecureState } from '../../../types/state'
import { SECURESTATE } from '../../state'
import { pubSubSecureState } from '../../state/pubSub'

const getSecureState =
    <T extends keyof SecureState>(key: T) =>
    () =>
        SECURESTATE[key]

const getSecureSelectorState =
    <T extends keyof SecureState, K extends keyof SecureState[T]>(
        key: T,
        valueKey: K,
    ) =>
    () =>
        SECURESTATE[key][valueKey]

const subSecureState =
    <T extends keyof SecureState>(key: T) =>
    (callback: () => void) =>
        pubSubSecureState.on(key, callback)

export const useSecureState = <T extends keyof SecureState>(key: T) => {
    const state = useSyncExternalStore<SecureState[T]>(
        subSecureState(key),
        getSecureState(key),
        getSecureState(key),
    )

    return state
}

/**
 * Subscribes to one field of a secure-state entry. The store still notifies
 * on every change to the entry, but React only re-renders when the selected
 * value changes — use this over `useSecureState` in components that would
 * otherwise re-render on unrelated fields (e.g. every grid tile on the
 * hourly image-token refresh).
 */
export const useSecureStateSelector = <
    T extends keyof SecureState,
    K extends keyof SecureState[T],
>(
    key: T,
    valueKey: K,
): SecureState[T][K] =>
    useSyncExternalStore(
        subSecureState(key),
        getSecureSelectorState(key, valueKey),
        getSecureSelectorState(key, valueKey),
    )
